import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  FileText,
  FolderOpen,
  MessageSquare,
  LayoutGrid,
  Eye,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useMatter } from '../hooks/useMatter';
import { hasSponsorForm, sponsorRoleLabel, sponsorRoleNoun } from '../lib/schema-runtime';
import type { EngagementLetterState } from '../lib/engagementLetter';

interface MatterDetail {
  id: string;
  matter_id: string | null;
  visa_subclass: string;
  variant: string | null;
  relationship_status: string | null;
  status: string;
  lodgement_date: string | null;
  rfi_deadline: string | null;
  initial_payment_date: string | null;
  final_payment_date: string | null;
  trn: string | null;
  drive_folder_url: string | null;
  ghl_opportunity_id: string | null;
  engagement_letter: EngagementLetterState | null;
  primary_contact: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  sponsor_contact: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  assigned_staff: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
  reviewer_staff?: { id: string; given_names: string | null; family_name: string | null; email: string } | null;
}

export default function MatterDetailLayout() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setActiveMatterId, isStaff, setImpersonating } = useMatter();
  const [matter, setMatter] = useState<MatterDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const matterId = params.id;

  // Sync URL → MatterContext.activeMatter so child tabs (Intake/Documents/Messages)
  // that rely on useMatter() pick up the right matter automatically.
  useEffect(() => {
    if (matterId) setActiveMatterId(matterId);
  }, [matterId, setActiveMatterId]);

  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from('matters')
          .select(
            `id, matter_id, visa_subclass, variant, relationship_status, status, lodgement_date, rfi_deadline,
             initial_payment_date, final_payment_date, trn, drive_folder_url, ghl_opportunity_id,
             engagement_letter,
             primary_contact:contacts!matters_primary_contact_id_fkey (id, given_names, family_name, email),
             sponsor_contact:contacts!matters_sponsor_contact_id_fkey (id, given_names, family_name, email),
             assigned_staff:contacts!matters_assigned_staff_id_fkey (id, given_names, family_name, email),
             reviewer_staff:contacts!matters_reviewer_staff_id_fkey (id, given_names, family_name, email)`,
          )
          .eq('id', matterId)
          .is('deleted_at', null)
          .maybeSingle();
        if (err) throw err;
        if (!cancelled) setMatter(data as unknown as MatterDetail);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load matter');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matterId]);

  if (loading) {
    return <div className="text-sm text-slate-500">Loading matter...</div>;
  }
  if (error || !matter) {
    return (
      <div className="card p-6 text-sm text-red-600">
        Couldn't load matter: {error ?? 'not found'}
      </div>
    );
  }

  const clientName =
    [matter.primary_contact?.given_names, matter.primary_contact?.family_name].filter(Boolean).join(' ').trim() ||
    matter.primary_contact?.email ||
    'Unknown client';
  const visaLabel = `${matter.visa_subclass}${matter.variant ? ` ${matter.variant}` : ''}`;

  return (
    <div className="space-y-4">
      {/* Matter header. Desktop fits on one row, mobile stacks into a clean
          breadcrumb / identity-and-meta / actions sequence. */}
      <div className="space-y-2">
        <Link
          to="/matters"
          className="text-xs text-slate-500 hover:text-[#0B2341] inline-flex items-center gap-1"
        >
          <ChevronLeft size={12} /> All matters
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:gap-3">
          <div className="flex flex-1 min-w-0 items-center flex-wrap gap-x-2.5 gap-y-1">
            <span className="font-mono text-xs text-slate-500">
              {matter.matter_id ?? 'Pending matter ID'}
            </span>
            {matter.primary_contact?.id ? (
              <Link
                to={`/contacts/${matter.primary_contact.id}`}
                className="text-lg font-bold text-[#0B2341] truncate hover:text-[#2CABEA] hover:underline"
              >
                {clientName}
              </Link>
            ) : (
              <h1 className="text-lg font-bold text-[#0B2341] truncate">{clientName}</h1>
            )}
            <span className="text-slate-300">·</span>
            <span className="text-sm text-slate-700">{visaLabel}</span>
            <span className="text-slate-300">·</span>
            <StatusChip status={matter.status} />
            {matter.lodgement_date && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-xs text-slate-500">
                  Lodgement <span className="font-medium text-[#0B2341]">{formatDate(matter.lodgement_date)}</span>
                </span>
              </>
            )}
            {matter.rfi_deadline && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-xs text-amber-600">
                  RFI <span className="font-medium">{formatDate(matter.rfi_deadline)}</span>
                </span>
              </>
            )}
          </div>

        {/* Right-aligned: View-as pill cluster (staff only). Stub action buttons removed. */}
        {isStaff && matter.primary_contact && (
          <div className="inline-flex items-center gap-1 shrink-0 rounded-md border border-slate-200 bg-slate-50 p-0.5 mt-2 sm:mt-0 self-start">
            <button
              type="button"
              onClick={() => {
                setImpersonating(matter.id, 'applicant');
                navigate('/dashboard');
              }}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded text-slate-700 hover:bg-white hover:text-[#0B2341] transition-colors"
              title={
                matter.primary_contact
                  ? `View as ${[matter.primary_contact.given_names, matter.primary_contact.family_name].filter(Boolean).join(' ')}`
                  : 'View as applicant'
              }
            >
              <Eye size={12} /> Applicant
            </button>
            {matter.sponsor_contact && hasSponsorForm(matter.visa_subclass) && (
              <>
                <span className="text-slate-300">|</span>
                <button
                  type="button"
                  onClick={() => {
                    setImpersonating(matter.id, 'sponsor');
                    navigate('/dashboard');
                  }}
                  className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded text-slate-700 hover:bg-white hover:text-[#0B2341] transition-colors"
                  title={
                    matter.sponsor_contact
                      ? `View as ${[matter.sponsor_contact.given_names, matter.sponsor_contact.family_name].filter(Boolean).join(' ')}`
                      : `View as ${sponsorRoleNoun(matter.visa_subclass)}`
                  }
                >
                  <Eye size={12} /> {sponsorRoleLabel(matter.visa_subclass)}
                </button>
              </>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Tab strip — placeholder tabs (Notes/Tasks/Timeline/Lodgement) hidden until they ship */}
      <div className="border-b border-slate-200 -mx-2 px-2 overflow-x-auto">
        <nav className="flex items-center gap-1 min-w-max">
          <Tab to="" end icon={<LayoutGrid size={14} />}>Overview</Tab>
          <Tab to="intake" icon={<FileText size={14} />}>Intake</Tab>
          {['820', '801', '309', '100', '300'].includes(matter.visa_subclass) && (
            <Tab to="form-80" icon={<FileText size={14} />}>Form 80</Tab>
          )}
          <Tab to="documents" icon={<FolderOpen size={14} />}>Documents</Tab>
          <Tab to="messages" icon={<MessageSquare size={14} />}>Contact</Tab>
        </nav>
      </div>

      {/* Tab content */}
      <div className="pt-2">
        <Outlet context={{ matter }} />
      </div>
    </div>
  );
}

function Tab({
  to,
  end = false,
  icon,
  children,
}: {
  to: string;
  end?: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-[1px] ${
          isActive
            ? 'border-[#2CABEA] text-[#0B2341]'
            : 'border-transparent text-slate-500 hover:text-[#0B2341]'
        }`
      }
    >
      {icon} {children}
    </NavLink>
  );
}

function StatusChip({ status }: { status: string }) {
  const lower = status.toLowerCase();
  let cls = 'bg-slate-100 text-slate-700';
  if (lower.includes('block')) cls = 'bg-red-100 text-red-700';
  else if (lower.includes('await')) cls = 'bg-amber-100 text-amber-800';
  else if (lower.includes('progress')) cls = 'bg-blue-100 text-blue-700';
  else if (lower.includes('lodge') || lower.includes('grant')) cls = 'bg-green-100 text-green-700';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${cls}`}>
      {status}
    </span>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}
